import React from "react";
import { Content } from "@/models/document";
import { Card, CardBody, Chip, Typography } from "@/components/ui";
import { FaClock, FaGlobe, FaShare, FaHeart, FaComment, FaRetweet } from "react-icons/fa";
import useTranslation from "@/hooks/use-translation";
import { safeString } from "@/utils/safe-value";

interface MasonryViewProps {
  items: Content[];
  displayMode?: string;
}

const MasonryView: React.FC<MasonryViewProps> = ({
  items,
  displayMode = "normal",
}) => {
  const { t } = useTranslation();

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getAuthorInfo = (item: any) => {
    // If item has normalized, use normalized; otherwise use item directly
    const rawData = item.normalized ? item.normalized : item;
    return {
      username: safeString(rawData.author?.username || rawData.author?.title) || "Unknown",
      avatar: safeString(rawData.author?.media?.[0]?.url || rawData.author?.avatar),
      verified: rawData.author?.verified,
    };
  };

  const getContentInfo = (item: any) => {
    // If item has normalized, use normalized; otherwise use item directly
    const rawData = item.normalized ? item.normalized : item;
    return {
      title: safeString(rawData.title),
      text: safeString(rawData.text || rawData.description),
      date: safeString(rawData.date || rawData.published_time),
      language: safeString(rawData.language || rawData.lang || item.extra?.lang),
      url: safeString(rawData.url),
      stats: rawData.stats,
      media: rawData.media && rawData.media.length > 0
        ? rawData.media
        : rawData.image || rawData.thumbnail
          ? [
              {
                type: "image",
                url: safeString(rawData.image || rawData.thumbnail),
                thumbnail: safeString(rawData.thumbnail),
              },
            ]
          : [],
    };
  };

  const getStatsIcon = (key: string) => {
    switch (key) {
      case "likes":
        return <FaHeart className="w-4 h-4" />;
      case "comments":
        return <FaComment className="w-4 h-4" />;
      case "shares":
        return <FaRetweet className="w-4 h-4" />;
      default:
        return null;
    }
  };

  return (
    <div className="columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-6 space-y-6">
      {items.map((item, index) => {
        const authorInfo = getAuthorInfo(item);
        const contentInfo = getContentInfo(item);

        return (
          <div key={item.id || index} className="break-inside-avoid mb-6">
            <Card
              className="hover:shadow-arc-lg transition-all duration-300 cursor-pointer"
              onClick={() =>
                contentInfo.url && window.open(contentInfo.url, "_blank")
              }
            >
              <CardBody className="p-4">
                {/* Header avec auteur */}
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-surface-secondary">
                    {authorInfo.avatar ? (
                      <img
                        src={authorInfo.avatar}
                        alt={authorInfo.username}
                        className="size-full object-cover"
                      />
                    ) : (
                      <div className="size-full flex items-center justify-center text-content-tertiary text-sm font-semibold">
                        {authorInfo.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <Typography variant="text-sm" className="font-semibold truncate">
                        {authorInfo.username}
                      </Typography>
                      {authorInfo.verified && (
                        <Chip size="sm" variant="soft" color="info">✓</Chip>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 text-content-tertiary">
                      <FaClock className="w-3 h-3" />
                      <Typography variant="text-xs">
                        {contentInfo.date && formatDate(contentInfo.date)}
                      </Typography>
                    </div>
                  </div>
                </div>

                {/* Média */}
                {contentInfo.media && contentInfo.media.length > 0 && (
                  <div className="mb-3">
                    <div className="relative rounded-arc-lg overflow-hidden bg-surface-secondary">
                      <img
                        src={
                          contentInfo.media[0].url ||
                          contentInfo.media[0].thumbnail
                        }
                        alt={contentInfo.title}
                        className="w-full h-auto object-cover"
                      />
                    </div>
                  </div>
                )}

                {/* Contenu */}
                <div>
                  {contentInfo.title && (
                    <Typography variant="text-sm" className="font-semibold mb-2 line-clamp-2">
                      {contentInfo.title}
                    </Typography>
                  )}
                  <Typography variant="text-sm" className="leading-relaxed" color="secondary">
                    {contentInfo.text}
                  </Typography>
                </div>

                {/* Footer avec stats */}
                <div className="mt-4 pt-3 border-t border-arc-border">
                  {contentInfo.stats && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {Object.entries(contentInfo.stats)
                        .slice(0, 3)
                        .map(([key, value]) => {
                          if (value === undefined || value === null) return null;
                          return (
                            <div
                              key={key}
                              className="flex items-center space-x-1"
                            >
                              {getStatsIcon(key)}
                              <Typography variant="text-xs" className="text-content-secondary font-medium">
                                {typeof value === "number"
                                  ? value.toLocaleString()
                                  : String(value)}
                              </Typography>
                            </div>
                          );
                        })}
                    </div>
                  )}

                  {/* Métadonnées */}
                  <div className="flex items-center justify-between text-xs text-content-tertiary">
                    <div className="flex items-center space-x-2">
                      {contentInfo.language && (
                        <div className="flex items-center space-x-1">
                          <FaGlobe className="w-3 h-3" />
                          <span>{contentInfo.language.toUpperCase()}</span>
                        </div>
                      )}
                    </div>
                    {contentInfo.url && (
                      <div className="flex items-center space-x-1 text-accent">
                        <FaShare className="w-3 h-3" />
                        <span>{t("view_original")}</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        );
      })}
    </div>
  );
};

export default MasonryView;
